import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const transparentBar = open || (pathname === "/" && !scrolled);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          transparentBar ? "bg-transparent py-5" : "bg-[#FAFAF7]/90 backdrop-blur-xl border-b border-[#1A1A1A]/5 py-3"
        }`}
      >
        <div className="container-lux flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" onClick={() => setOpen(false)} className="flex items-center">
            <img src="/logo.png" alt="DonoDidio Event Management" className="h-12 md:h-16 w-auto" />
          </Link>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            className={`inline-flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-full border transition-colors ${
              open
                ? "border-[#F7F6F1]/60 text-[#F7F6F1] hover:bg-white/10"
                : transparentBar
                ? "border-white/60 text-white hover:bg-white/10"
                : "border-[#47562F]/40 text-[#47562F] hover:bg-[#47562F]/10"
            }`}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* Full-screen overlay menu (sibling of header so it isn't trapped by backdrop-blur) */}
      <div
        data-testid="nav-overlay"
        className={`fixed inset-0 z-40 bg-[#2C3520] flex flex-col items-center justify-center gap-6 px-8 transition-opacity duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <nav className="flex flex-col items-center gap-5 md:gap-6">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`font-serif-lux text-3xl md:text-4xl transition-colors ${
                pathname === l.to ? "text-[#D4AF37]" : "text-[#F7F6F1] hover:text-[#D4AF37]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <button
          data-testid="nav-quote-btn"
          onClick={() => navigate("/quote")}
          className="btn-gold mt-6 !bg-[#F7F6F1] !text-[#333F22] hover:!bg-[#D4AF37] hover:!text-[#0A0A0A]"
        >
          Begin Your Journey
        </button>
        <Link
          to="/staff-login"
          data-testid="nav-staff-link"
          className="mt-3 text-[0.68rem] uppercase tracking-[0.3em] text-[#F7F6F1]/55 hover:text-[#D4AF37] transition-colors"
        >
          Staff Login
        </Link>
      </div>
    </>
  );
}
